import React from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate, spring } from 'remotion';
import type { Caption, CaptionWord } from '../../schemas';

interface CaptionStyleProps {
  fontFamily?: string;
  fontSize?: number;
  color?: string;
  highlightColor?: string;
  position?: 'top' | 'center' | 'bottom';
  backgroundColor?: string;
}

interface AnimatedCaptionProps {
  captions?: Caption;
  style?: CaptionStyleProps;
  animation?: 'none' | 'word-highlight' | 'word-pop' | 'karaoke';
}

export const AnimatedCaption: React.FC<AnimatedCaptionProps> = ({
  captions,
  style = {},
  animation = 'word-highlight',
}) => {
  const frame = useCurrentFrame();
  const { fps, height } = useVideoConfig();

  const {
    fontFamily = 'Inter',
    fontSize = 48,
    color = '#ffffff',
    highlightColor = '#ffd700',
    position = 'bottom',
    backgroundColor,
  } = style;

  if (!captions?.words?.length) {
    return null;
  }

  // Convert frame to milliseconds
  const currentTimeMs = (frame / fps) * 1000;

  // Get current words to display (show a few words at a time)
  const getVisibleWords = (): { words: CaptionWord[]; activeIndex: number } => {
    const allWords = captions.words;

    // Find current active word
    let activeIndex = -1;
    for (let i = 0; i < allWords.length; i++) {
      if (currentTimeMs >= allWords[i].startMs && currentTimeMs <= allWords[i].endMs) {
        activeIndex = i;
        break;
      }
      if (currentTimeMs < allWords[i].startMs) {
        activeIndex = Math.max(0, i - 1);
        break;
      }
    }
    if (activeIndex === -1 && allWords.length > 0) {
      activeIndex = allWords.length - 1;
    }

    // Show a window of words around the active word
    const windowSize = 6;
    const startIdx = Math.max(0, activeIndex - 2);
    const endIdx = Math.min(allWords.length, startIdx + windowSize);

    return {
      words: allWords.slice(startIdx, endIdx),
      activeIndex: activeIndex - startIdx,
    };
  };

  const { words, activeIndex } = getVisibleWords();

  // Position styles
  const getPositionStyle = (): React.CSSProperties => {
    const padding = Math.round(height * 0.05);
    switch (position) {
      case 'top':
        return { top: padding, bottom: 'auto' };
      case 'center':
        return { top: '50%', transform: 'translateY(-50%)' };
      case 'bottom':
      default:
        return { bottom: padding, top: 'auto' };
    }
  };

  // Render word with animation
  const renderWord = (word: CaptionWord, index: number) => {
    const isActive = index === activeIndex;
    const isPast = currentTimeMs > word.endMs;
    const wordStartFrame = (word.startMs / 1000) * fps;

    let wordStyle: React.CSSProperties = {
      display: 'inline-block',
      marginRight: '0.3em',
      color: isActive ? highlightColor : color,
    };

    if (animation === 'word-pop' && isActive) {
      const scale = spring({
        frame: frame - wordStartFrame,
        fps,
        config: {
          damping: 12,
          stiffness: 200,
          mass: 0.5,
        },
      });
      wordStyle = {
        ...wordStyle,
        transform: `scale(${interpolate(scale, [0, 1], [0.8, 1.1])})`,
      };
    }

    if (animation === 'karaoke') {
      const progress = interpolate(
        currentTimeMs,
        [word.startMs, word.endMs],
        [0, 100],
        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
      );
      wordStyle = {
        ...wordStyle,
        background: `linear-gradient(90deg, ${highlightColor} ${progress}%, ${color} ${progress}%)`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
      };
    }

    if (isPast && animation !== 'karaoke') {
      wordStyle = {
        ...wordStyle,
        opacity: 0.7,
      };
    }

    return (
      <span key={`${word.text}-${index}`} style={wordStyle}>
        {word.text}
      </span>
    );
  };

  return (
    <AbsoluteFill
      style={{
        ...getPositionStyle(),
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '0 5%',
      }}
    >
      <div
        style={{
          fontFamily,
          fontSize,
          fontWeight: 700,
          textAlign: 'center',
          lineHeight: 1.3,
          textShadow: '2px 2px 8px rgba(0,0,0,0.8)',
          ...(backgroundColor && {
            backgroundColor,
            padding: '16px 24px',
            borderRadius: 12,
          }),
        }}
      >
        {words.map((word, index) => renderWord(word, index))}
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
